import {Observable} from 'rxjs';
import {
    CpmCTaskCreateApiSchema,
    CpmCTaskDeleteApiSchema,
    CpmCTaskReorderApiSchema,
    CpmCTaskUpdateApiSchema
} from '../../schemas/c-customer/cpmc-task.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCTaskApiModel} from '../../models/c-customer/cpmc-task.api-model';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCTaskExecutionApiModel} from '../../models/c-customer/cpmc-task-execution.api-model';
import {CpmCTaskTreeApiView} from '../../views/c-customer/cpmc-task-tree.api-view';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetTasksParams = {
    [K in keyof CpmCTaskApiModel]?: CpmCTaskApiModel[K] extends AllowedQueryParamTypes ? CpmCTaskApiModel[K] : never;
} & {} & QueryParams;

/**
 * Task Api Service
 */
export class CpmCTaskApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'order', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined, inServiceRequestUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();
        const serviceRequestUUID = CpmCApiBridge.getCurrentServiceRequestUUID();

        return (
            base +
            `customer/${inCustomerUUID || customerUUID}/service-request/${inServiceRequestUUID || serviceRequestUUID}/task`
        );
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined, inServiceRequestUUID: string | undefined): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/list`;
    }
    public static endpointTree(inCustomerUUID: string | undefined, inServiceRequestUUID: string | undefined): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/tree`;
    }
    public static endpointNew(inCustomerUUID: string | undefined, inServiceRequestUUID: string | undefined): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}`;
    }
    public static endpointGet(
        inCustomerUUID: string | undefined,
        inServiceRequestUUID: string | undefined,
        inTaskUUID: string
    ): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/${inTaskUUID}`;
    }
    public static endpointEdit(
        inCustomerUUID: string | undefined,
        inServiceRequestUUID: string | undefined,
        inTaskUUID: string
    ): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/${inTaskUUID}`;
    }
    public static endpointReorder(
        inCustomerUUID: string | undefined,
        inServiceRequestUUID: string | undefined,
        inTaskUUID: string
    ): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/${inTaskUUID}/reorder`;
    }
    public static endpointExecute(
        inCustomerUUID: string | undefined,
        inServiceRequestUUID: string | undefined,
        inTaskUUID: string
    ): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/${inTaskUUID}/execute`;
    }
    public static endpointDelete(
        inCustomerUUID: string | undefined,
        inServiceRequestUUID: string | undefined,
        inTaskUUID: string
    ): string {
        return `${CpmCTaskApiService.baseUrl(inCustomerUUID, inServiceRequestUUID)}/${inTaskUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getTaskList(
        params: CpmCGetTasksParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCTaskApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCTaskApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCTaskApiModel>>(
            CpmCTaskApiService.endpointList(undefined, undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getTaskTree(
        params: CpmCGetTasksParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCTaskApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCTaskTreeApiView>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCTaskTreeApiView>>(
            CpmCTaskApiService.endpointTree(undefined, undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getTaskByUUID(taskUUID: string): Observable<CpmCTaskApiModel> {
        return CpmCApiBridge.httpGet<CpmCTaskApiModel>(CpmCTaskApiService.endpointGet(undefined, undefined, taskUUID));
    }
    public static createTask(payload: CpmCTaskCreateApiSchema): Observable<CpmCTaskApiModel> {
        return CpmCApiBridge.httpPost<CpmCTaskApiModel>(CpmCTaskApiService.endpointNew(undefined, undefined), payload);
    }
    public static updateTask(taskUUID: string, payload: CpmCTaskUpdateApiSchema): Observable<CpmCTaskApiModel> {
        return CpmCApiBridge.httpPut<CpmCTaskApiModel>(
            CpmCTaskApiService.endpointEdit(undefined, undefined, taskUUID),
            payload
        );
    }
    public static reorderTask(taskUUID: string, payload: CpmCTaskReorderApiSchema): Observable<CpmCTaskApiModel> {
        return CpmCApiBridge.httpPost<CpmCTaskApiModel>(
            CpmCTaskApiService.endpointReorder(undefined, undefined, taskUUID),
            payload
        );
    }
    public static executeTask(taskUUID: string): Observable<CpmCTaskExecutionApiModel> {
        return CpmCApiBridge.httpPost<CpmCTaskExecutionApiModel>(
            CpmCTaskApiService.endpointExecute(undefined, undefined, taskUUID),
            {}
        );
    }
    public static deleteTask(taskUUID: string, payload?: CpmCTaskDeleteApiSchema): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCTaskApiService.endpointDelete(undefined, undefined, taskUUID),
            undefined,
            payload
        );
    }
}
